import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    BoltIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    CpuChipIcon
} from '@heroicons/react/24/outline';

const API_URL = '/api/v1';

export default function IssuanceRequest() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [devices, setDevices] = useState([]);

    const [formData, setFormData] = useState({
        deviceId: '',
        startDate: '',
        endDate: '',
        totalMwh: '',
        usageCategory: 'GRID_INJECTED',
        consumptionDeclaration: false
    });

    useEffect(() => {
        const fetchDevices = async () => {
            try {
                const response = await axios.get(`${API_URL}/devices`);
                setDevices(response.data);
            } catch (err) {
                console.error('Error fetching devices:', err);
            }
        };
        fetchDevices();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const selectedDevice = devices.find(d => d.id === formData.deviceId);
        if (!selectedDevice) {
            setError('Please select a production device.');
            setLoading(false);
            return;
        }

        if (!formData.consumptionDeclaration) {
            setError('You must agree to the Consumption Declaration.');
            setLoading(false);
            return;
        }

        try {
            const payload = {
                energy_source_code: selectedDevice.energy_source_code,
                technology_code: selectedDevice.technology_code,
                energy_medium_code: 'E',
                capacity_mw: parseFloat(selectedDevice.capacity_mw),
                output_mwh: parseFloat(formData.totalMwh),
                production_start: new Date(formData.startDate).toISOString(),
                production_end: new Date(formData.endDate).toISOString(),
                production_device_id: formData.deviceId,
                account_holder_id: selectedDevice.owner_id,
                usage_category: formData.usageCategory,
                purpose: 'EECS Issuance'
            };
            await axios.post(`${API_URL}/certificates`, payload);
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Issuance request failed. Check ledger constraints.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="max-w-2xl mx-auto py-20 text-center animate-fade-in">
                <div className="w-24 h-24 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_0_40px_rgba(16,185,129,0.2)]">
                    <CheckCircleIcon className="w-12 h-12 text-emerald-500" />
                </div>
                <h2 className="text-4xl font-black text-gray-800 mb-4 tracking-tighter uppercase">Ledger Minting Successful</h2>
                <p className="text-gray-500 mb-8 font-medium">Guarantees of Origin have been successfully minted and deposited into your Primary Account.</p>
                <button onClick={() => window.location.href = '/certificates'} className="px-10 py-4 bg-[#155e75] text-white text-[11px] font-black uppercase tracking-widest hover:bg-[#0e4e61] transition-all rounded-sm shadow-xl">
                    View Digital Wallet
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto py-12 px-6">
            <div className="mb-10 text-center">
                <h1 className="text-4xl font-black text-gray-800 tracking-tighter uppercase">Request Issuance</h1>
                <p className="text-gray-500 font-medium mt-2">Submit meter readings to mint Guarantees of Origin (GOs)</p>
            </div>

            {error && (
                <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-sm flex items-center gap-3">
                    <ExclamationCircleIcon className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <p className="text-red-700 text-xs font-bold uppercase tracking-widest">{error}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8 animate-fade-in">
                <div className="glass-card bg-white p-10 border border-gray-200 rounded-sm shadow-xl">
                    <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-100">
                        <BoltIcon className="w-6 h-6 text-[#155e75]" />
                        <h3 className="text-xl font-bold text-gray-800 uppercase tracking-tight">Production Data</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="md:col-span-2 space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Select Registered Plant</label>
                            <select required name="deviceId" value={formData.deviceId} onChange={handleChange} className="w-full p-4 bg-gray-50 border border-gray-200 outline-none focus:border-[#155e75] focus:bg-white transition-all text-xs font-bold text-gray-700 rounded-none appearance-none">
                                <option value="">Select a device...</option>
                                {devices.map(d => (
                                    <option key={d.id} value={d.id}>{d.device_name} ({d.capacity_mw} MW) - {d.status} {d.is_autoproduction ? '[AUTOPROD]' : ''}</option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Start Date</label>
                            <input required name="startDate" value={formData.startDate} onChange={handleChange} type="date" className="w-full p-4 bg-gray-50 border border-gray-200 outline-none focus:border-[#155e75] focus:bg-white transition-all text-xs font-bold text-gray-700 rounded-none" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">End Date</label>
                            <input required name="endDate" value={formData.endDate} onChange={handleChange} type="date" className="w-full p-4 bg-gray-50 border border-gray-200 outline-none focus:border-[#155e75] focus:bg-white transition-all text-xs font-bold text-gray-700 rounded-none" />
                        </div>

                        <div className="md:col-span-2 p-6 bg-blue-50/50 border border-blue-100 space-y-4">
                            <div className="flex items-center gap-2 mb-2">
                                <CpuChipIcon className="w-5 h-5 text-blue-600" />
                                <h4 className="text-[10px] font-black text-blue-800 uppercase tracking-widest">Market Classification (Law 82-21)</h4>
                            </div>
                            <div className="flex gap-4">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, usageCategory: 'GRID_INJECTED' })}
                                    className={`flex-1 p-4 border transition-all text-left ${formData.usageCategory === 'GRID_INJECTED' ? 'bg-white border-[#155e75] shadow-md ring-2 ring-[#155e75]/10' : 'bg-gray-100/50 border-transparent text-gray-400 opacity-60'}`}
                                >
                                    <p className="text-[10px] font-black uppercase tracking-tight">Grid Injected</p>
                                    <p className="text-[8px] font-bold">Standard Market Sale</p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, usageCategory: 'SELF_CONSUMPTION' })}
                                    className={`flex-1 p-4 border transition-all text-left ${formData.usageCategory === 'SELF_CONSUMPTION' ? 'bg-white border-indigo-600 shadow-md ring-2 ring-indigo-600/10' : 'bg-gray-100/50 border-transparent text-gray-400 opacity-60'}`}
                                >
                                    <p className="text-[10px] font-black uppercase text-indigo-700 tracking-tight">Self-Consumption</p>
                                    <p className="text-[8px] font-bold">Hydrogen / BTM Claims</p>
                                </button>
                            </div>
                        </div>

                        <div className="md:col-span-2 space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Total Net Output (MWh)</label>
                            <div className="relative">
                                <input required name="totalMwh" value={formData.totalMwh} onChange={handleChange} type="number" step="0.001" placeholder="0.000" className="w-full p-4 pr-16 bg-gray-50 border border-gray-200 outline-none focus:border-[#155e75] focus:bg-white transition-all text-xs font-bold font-mono text-[#155e75] rounded-none" />
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-[10px] uppercase tracking-widest">MWh</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 p-8 rounded-sm text-gray-800">
                    <div className="flex items-start gap-4">
                        <input
                            type="checkbox"
                            name="consumptionDeclaration"
                            checked={formData.consumptionDeclaration}
                            onChange={handleChange}
                            className="mt-1 w-5 h-5 rounded border-gray-300 text-[#155e75] focus:ring-[#155e75] bg-white cursor-pointer"
                        />
                        <div>
                            <p className="text-xs font-black uppercase tracking-widest mb-1 text-gray-800">Consumption Declaration</p>
                            <p className="text-[10px] text-gray-500 font-bold leading-relaxed">
                                I hereby declare that the measured production data submitted is accurate,
                                and any energy consumed by Production Auxiliaries has undeniably been deducted
                                from the Net Output, according to the Domain Protocol regulations.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={loading || devices.length === 0}
                        className="bg-[#155e75] hover:bg-[#0e4e61] text-white px-12 py-4 rounded-sm text-[11px] font-black uppercase tracking-widest shadow-xl disabled:opacity-50 transition-all flex items-center justify-center min-w-[250px]"
                    >
                        {loading ? 'Processing...' : 'Confirm & Mint Certificates'}
                    </button>
                </div>
            </form>
        </div>
    );
}
